import { type FC } from 'react';
import { useFragment, graphql } from 'react-relay';

import { FormattedMessage } from 'dibs-react-intl';
import CarrierTrackingDetails from 'dibs-shipment-tracking/exports/CarrierTrackingDetails.ts';
import EstimatedDeliveryDateDisplay from 'dibs-shipment-tracking/exports/EstimatedDeliveryDateDisplay.tsx';
import EstimatedShippingDateDisplay from 'dibs-shipment-tracking/exports/EstimatedShippingDateDisplay.tsx';

import { type TrackingDetails_viewer$key } from './__generated__/TrackingDetails_viewer.graphql.ts';

import styles from './styles/TrackingDetails.scss';

const viewerFragment = graphql`
    fragment TrackingDetails_viewer on Viewer {
        ...EstimatedShippingDateDisplay_viewer
        ...EstimatedDeliveryDateDisplay_viewer
        transaction(transactionId: $transactionId) @include(if: $hasOrderId) {
            ...EstimatedDeliveryDateDisplay_transaction
            ...EstimatedShippingDateDisplay_transaction
            isCanceled
            shipment {
                ...CarrierTrackingDetails_shipment
                deliveryDateRangeDisplay
                estimatedShippingDate
                isBuyerShipping
                shipmentTracking {
                    lateByDate
                }
            }
            buyerTimelineStatuses {
                shipped
                completed
            }
        }
    }
`;

const TrackingDetails: FC<{
    viewer: TrackingDetails_viewer$key | null | undefined;
    isSeller: boolean;
}> = ({ viewer: viewerRef, isSeller }) => {
    const viewer = useFragment(viewerFragment, viewerRef);

    const { transaction } = viewer || {};
    const { shipment, isCanceled, buyerTimelineStatuses } = transaction || {};
    const { estimatedShippingDate, deliveryDateRangeDisplay, isBuyerShipping, shipmentTracking } =
        shipment || {};
    const { shipped, completed } = buyerTimelineStatuses || {};
    const { lateByDate } = shipmentTracking || {};

    let isLate = false;

    if (lateByDate) {
        const today = new Date();
        const lateBy = new Date(lateByDate);
        isLate = today.setHours(0, 0, 0, 0) > lateBy.setHours(0, 0, 0, 0);
    }

    const showShippingDelayedBanner = !isSeller && isLate && !isBuyerShipping && !isCanceled;

    return (
        <div>
            {showShippingDelayedBanner && (
                <div className={styles.shippingDelayed}>
                    <FormattedMessage
                        id="getHelp.ShippingDelayedBanner.info"
                        defaultMessage="<b>It appears that this item has not yet been shipped.</b> We recommend contacting the seller for details regarding the delay."
                        values={{
                            b: content => <b>{content}</b>,
                        }}
                    />
                </div>
            )}
            <div className={styles.datesWrapper}>
                {!shipped && estimatedShippingDate && (
                    <div>
                        <EstimatedShippingDateDisplay
                            viewer={viewer}
                            transaction={transaction || null}
                        />
                    </div>
                )}
                {deliveryDateRangeDisplay && !completed && (
                    <div>
                        <EstimatedDeliveryDateDisplay
                            transaction={transaction || null}
                            viewer={viewer}
                        />
                    </div>
                )}
            </div>
            {shipped && (
                <div className={styles.trackingInfo}>
                    <CarrierTrackingDetails
                        shipment={shipment || null}
                        trackTrackingDetailsDisplayed={!isSeller}
                        placement="Get Help Modal"
                    />
                </div>
            )}
        </div>
    );
};

export default TrackingDetails;
